import React from 'react'
import { usePushHistory } from '@/shared/hooks'
import type { PushHistoryItem } from '@/shared/api'
import { Button, Card, DataTable, OverflowText } from '@/shared/ui'

// dd.MM.yyyy HH:mm в локальном времени; createdAt может быть null у старых пушей
const formatDateTime = (seconds: number | null): string => {
  if (seconds === null || seconds === undefined) return '—'
  const date = new Date(seconds * 1000)
  if (Number.isNaN(date.getTime())) return '—'
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`
}

const STATUS_LABELS: Record<PushHistoryItem['status'], string> = {
  sent: 'Отправлено',
  error: 'Ошибка отправки',
}

const STATUS_CLASSES: Record<PushHistoryItem['status'], string> = {
  sent: 'bg-success-100 text-success-800',
  error: 'bg-error-100 text-error-800',
}

interface PushHistoryTableProps {
  page: number
  onPageChange: (page: number) => void
  onRepeat: (item: PushHistoryItem) => void
  canSend: boolean
}

export const PushHistoryTable: React.FC<PushHistoryTableProps> = ({
  page,
  onPageChange,
  onRepeat,
  canSend,
}) => {
  const { data, isLoading, isError } = usePushHistory(page)
  const items = data?.items ?? []

  return (
    <section className="flex-1 min-h-0 flex flex-col gap-3">
      <div>
        <h2 className="text-base font-semibold text-gray-900">История отправок</h2>
        <p className="mt-1 text-sm text-gray-500">
          Рассылки администраторов; персональные пуши курьерам не показываются
        </p>
      </div>

      {isError ? (
        <Card>
          <p className="text-sm text-red-600">Не удалось загрузить историю отправок</p>
        </Card>
      ) : (
        <DataTable
          data={items}
          loading={isLoading && !data}
          loadingMessage="Загрузка истории…"
          emptyMessage="Отправок пока нет"
          dense
          fixedLayout
          fillHeight
          className="flex-1 min-h-0"
          columns={[
            {
              key: 'createdAt',
              title: 'Дата и время',
              width: '16%',
              render: (_, item: PushHistoryItem) => formatDateTime(item.createdAt),
            },
            {
              key: 'message',
              title: 'Сообщение',
              width: '36%',
              render: (_, item: PushHistoryItem) => (
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">{item.title || '—'}</p>
                  {item.message ? (
                    <OverflowText text={item.message} maxLines={2} />
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              ),
            },
            {
              key: 'audienceLabel',
              title: 'Аудитория',
              width: '16%',
              render: (_, item: PushHistoryItem) => item.audienceLabel || '—',
            },
            {
              key: 'recipientsCount',
              title: 'Получателей',
              width: '10%',
              render: (_, item: PushHistoryItem) => item.recipientsCount ?? '—',
            },
            {
              key: 'status',
              title: 'Статус',
              width: '12%',
              render: (_, item: PushHistoryItem) => (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASSES[item.status]}`}
                >
                  {STATUS_LABELS[item.status] ?? item.status}
                </span>
              ),
            },
            {
              key: 'actions',
              title: 'Действия',
              width: '10%',
              render: (_, item: PushHistoryItem) =>
                item.kind === 'campaign' && canSend ? (
                  <Button variant="secondary" size="sm" onClick={() => onRepeat(item)}>
                    Повторить
                  </Button>
                ) : null,
            },
          ]}
          pagination={
            data
              ? {
                  page: data.page,
                  limit: data.limit,
                  total: data.total,
                  pages: data.pages,
                  onPageChange,
                }
              : undefined
          }
        />
      )}
    </section>
  )
}
